<template>
  <div id="login-screen" class="login">
    <transition
      name="fade"
      mode="out-in"
      appear
    >
      <!-- LOGIN CARD -->
      <LoginCard
        v-if="!is2faCardVisible"
        key="login-card"
      >
        <template #left-column>
          <picture class="login-card__illustration">
            <source
              media="(min-width:940px)"
              srcset="/images/login/login_desktop.svg"
            />
            <img
              src="/images/login/login_mobile.svg"
              title="Fretes práticos e baratos para quem vende online"
              alt="Ilustração de uma menina sorrindo e um menino segurando um pacote do Melhor Envio"
            />
          </picture>

          <h2 class="login__subtitle">Fretes práticos e baratos <br />para quem vende online</h2>
        </template>

        <template #right-column>
          <div class="login-card-content">
            <LoginCardHeading title="Acesse sua conta" />

            <form
              role="form"
              id="login-form"
              class="form"
            >
              <fieldset
                class="formLine"
                :class="{
                  active: username,
                  error: $v.username.$error,
                  focus: focus.username,
                }"
              >
                <legend class="text-hidden">Digite seu CPF ou e-mail</legend>
                <div class="inputBox">
                  <label for="username">CPF ou e-mail*</label>
                  <input
                    id="username"
                    type="text"
                    name="username"
                    v-model="username"
                    @focus="iptFocus('username', $event)"
                    @blur="iptBlur('username', $event)"
                    @input="$v.username.$touch"
                  />
                  <p class="errorText" v-html="errors.username" />
                </div>
              </fieldset>

              <fieldset
                class="formLine"
                :class="{
                  active: password,
                  focus: focus.password,
                  error: $v.password.$error,
                }"
              >
                <legend class="text-hidden">Digite sua senha</legend>
                <div class="inputBox">
                  <label for="password">Senha*</label>
                  <input
                    ref="passwordInput"
                    id="password"
                    :type="!passwordVisible ? 'password' : 'text' "
                    name="password"
                    v-model="password"
                    @focus="iptFocus('password', $event)"
                    @blur="iptBlur('password', $event)"
                    @input="$v.password.$touch"
                  />
                  <p class="errorText" v-html="errors.password" />
                </div>

                <button
                  type="button"
                  tabindex="-1"
                  class="input-password-toggle"
                  @click.prevent="togglePasswordVisible"
                >
                  <svg class="ico">
                    <use :xlink:href="`/images/svg-symbols.svg${passwordVisible ? '#eye-close' : '#eye-open'}`" />
                  </svg>
                </button>
              </fieldset>

              <p
                class="error-message"
                :class="{ 'error-message--visible': invalidLogin }"
                v-html="errorMessage"
              />

              <div class="keep-connected">
                <input type="checkbox" id="remember" v-model="remember" />
                <label for="remember">Mantenha-me conectado</label>
              </div>

              <h2>
                <button
                  name="Acessar o Melhor Envio"
                  type="submit"
                  class="button-new text-small mx-auto"
                  :class="activeSubmitButton"
                  :disabled="$v.username.$error || $v.password.$error"
                  @click.prevent="submit"
                >
                  Entrar
                </button>
              </h2>

              <h3 class="text-small text-center font-lato mt-4">
                <a
                  href="javascript:;"
                  title="Recuperar senha"
                  @click.prevent="resendPassword"
                >
                  Esqueceu sua senha?
                </a>
              </h3>
            </form>

            <LoginCardDisclaimer text="Ainda não economiza com o Melhor Envio?">
              <h3>
                <a
                  href="/cadastre-se"
                  title="Faça seu cadastro"
                >
                  Cadastre-se
                </a>
              </h3>
            </LoginCardDisclaimer>
          </div>
        </template>
      </LoginCard>

      <!-- 2FA CARD -->
      <LoginCard
        v-else
        key="2fa-card"
      >
        <template #left-column>
          <picture class="login-card__illustration">
            <source
              media="(min-width:940px)"
              srcset="/images/login/two_factor_desktop.svg"
            />
            <img
              src="/images/login/two_factor_mobile.svg"
              title="Acesso seguro"
              alt="Ilustração de uma menina apoiada sobre uma tela com a imagem de uma fechadura"
            />
          </picture>
        </template>

        <template #right-column>
          <transition
            name="fade"
            mode="out-in"
          >
            <component
              :is="current2FAStep"
              @step-changed="handle2faStep"
              @channel-changed="updateSelected2faChannel"
              @start-loading="handleLoading(true)"
              @finish-loading="handleLoading(false)"
              @handle-redirect="handleLoginRedirect"
              @handle-toast="handleToast"
            />
          </transition>
        </template>
      </LoginCard>
    </transition>

    <!-- FORGOT PASSWORD MODAL -->
    <modal-v2
      ref="passwordModal"
      customContent
      :size="emailSended ? 'medium' : ''"
      id="forget-password"
      @modalClosed="$v.iptPasswordReset.$reset"
    >
      <template
        v-if="!emailSended"
        #custom-content
      >
        <h1 class="modal-v2__header">Esqueci minha senha</h1>

        <div class="modal-v2__body">
          <p class="forgot-password-body">
            Informe o CPF/CNPJ de cadastro e vamos enviar um link de alteração de senha para o e-mail da conta.
          </p>

          <div id="cpf-field" class="form">
            <fieldset
              class="formLine mx-auto"
              :class="{
                active: iptPasswordReset,
                error: $v.iptPasswordReset.$error,
                focus: focus.iptPasswordReset,
              }"
            >
              <legend class="text-hidden">Digite seu CPF/CNPJ</legend>
              <div class="inputBox">
                <label for="iptPasswordReset">CPF/CNPJ</label>
                <input
                  name="iptPasswordReset"
                  id="iptPasswordReset"
                  v-model="iptPasswordReset"
                  v-mask="['###.###.###-##', '##.###.###/####-##']"
                  @focus="iptFocus('iptPasswordReset', $event)"
                  @blur="iptBlur('iptPasswordReset', $event)"
                  @input="$v.iptPasswordReset.$touch"
                />

                <p class="errorText" v-html="errors.iptPasswordReset" />
              </div>
            </fieldset>
          </div>
        </div>

        <div class="modal-v2__footer">
          <button
            name="Alterar senha"
            type="submit"
            class="button-new text-small"
            :class="activePasswordButton"
            :disabled="!iptPasswordReset || $v.iptPasswordReset.$error"
            @click.prevent="sendResetPassword"
          >
            Alterar senha
          </button>
        </div>
      </template>

      <template
        v-else
        #custom-content
      >
        <div class="custom-content__header">
          <img
            src="/images/registration/mail–illustration.svg"
            title="Confira o e-mail cadastrado"
            alt="Ilustração de uma mulher apontando para um envelope de confirmação de recebimento do e-mail"
          />

          <h1>Confira o e-mail cadastrado</h1>

          <p>
            Recebemos sua solicitação para recuperar a senha.
            Caso exista alguma conta relacionada a este CPF/CNPJ, vamos enviar um link
            para o e-mail de cadastro com as instruções para recuperar a senha.
          </p>
        </div>

        <div class="custom-content__body">
          <div class="custom-content__text-wrapper">
            <svg class="alert-icon">
              <use xlink:href="/images/svg-symbols.svg#alert-new"></use>
            </svg>

            <p class="custom-content__text">
              O link de alteração expira em {{passwordTokenExpire}}h. Se você não conseguir alterar a senha
              dentro desse período, solicite uma nova alteração de senha.
            </p>
          </div>

          <p class="custom-content__text custom-content__text--small">
            Não recebeu o e-mail? Sugerimos que procure em sua caixa de Spam
            ou confira se o CPF/CNPJ foi informado corretamente. Se desejar,
            <a href="javascript:;" @click="sendResetPassword">clique aqui</a>
            para reenviar.
          </p>
        </div>

        <div class="custom-content__footer">
          <button
            name="Entendi"
            type="submit"
            class="button-new button-new--primary text-small"
            @click="$refs.passwordModal.close()"
          >
            Entendi
          </button>
        </div>
      </template>
    </modal-v2>

    <!-- INACTIVATION AND REVALIDATION MODAL -->
     <modal-v2
      ref="revalidationModal"
      customContent
      id="revalidation-password"
     >
      <template #custom-content>
        <div class="custom-content__header">
          <img
            src="/images/registration/mail-sender.svg"
            title="Confira o e-mail cadastrado"
            alt="Ilustração de uma carta com um documento checado e dois aviões de papel amarelos voando."
          />

          <h1>Verifique o e-mail de cadastro</h1>

          <p>{{ errorMessages.abandonedAccount }}</p>
        </div>

        <div class="custom-content__footer">
          <button
            name="Entendi"
            type="submit"
            class="button-new button-new--primary text-small"
            @click="$refs.revalidationModal.close()"
          >
            Entendi
          </button>
        </div>
      </template>
     </modal-v2>

    <!-- TOASTS -->
    <stacked-toast
      ref="loginToast"
      :timeout="10000"
    />

    <!-- ME LOADER -->
    <me-loading :loading="loading" />
  </div>
</template>

<script>
import { required, minLength, maxLength } from "vuelidate/lib/validators";
import recaptcha from "commons/js/ReCAPTCHAv3";
import cpf from "commons/js/validators/cpf";
import cnpj from "commons/js/validators/cnpj";
import documents from "commons/js/Documents";
import { mask } from "vue-the-mask";
import ModalV2 from "commons/js/components/atoms/ModalV2.vue";
import StackedToast from "commons/js/components/atoms/StackedToast.vue";
import LoginCard from "@home/js/components/login/LoginCard";
import LoginCardHeading from "@home/js/components/login/LoginCardHeading";
import LoginCardDisclaimer from "@home/js/components/login/LoginCardDisclaimer";

// 2FA
import {
  TWO_FACTOR_SERVICES_ENUM,
  TWO_FACTOR_LOGIN_STEPS_ENUM
} from "@home/js/components/login/2FA/config";
import VerificationCodeOptions from "@home/js/components/login/2FA/VerificationCodeOptions";
import VerificationCode from "@home/js/components/login/2FA/VerificationCode";
import ReservationCode from "@home/js/components/login/2FA/ReservationCode";
import { errorMessages } from "commons/js/components/forms/messages/inputValidations";

export default {
  components: {
    ModalV2,
    StackedToast,
    LoginCard,
    LoginCardHeading,
    LoginCardDisclaimer,
    VerificationCodeOptions,
    VerificationCode,
    ReservationCode
  },

  data() {
    return {
      userEmail: undefined,
      userPhone: undefined,

      is2faCardVisible: false,
      selected2faChannel: TWO_FACTOR_SERVICES_ENUM.EMAIL,
      current2FAStep: TWO_FACTOR_LOGIN_STEPS_ENUM.FIRST,
      redirectTo: '',

      passwordVisible: false,
      passwordTokenExpire: parseInt(MEConfig.get('password.token-expires'))/60,

      username: "",
      password: "",
      remember: true,

      iptPasswordReset: "",
      emailSended: false,
      modalPasswordReset: false,

      cpfNotFound: false,
      errorMessage: "",
      invalidLogin: false,
      errorMessages: {
        required: "Campo obrigatório! Não esqueça de preenchê-lo.",
        requiredIf: "Campo obrigatório! Não esqueça de preenchê-lo.",
        document: "CPF/CNPJ inválido. Por favor, tente novamente.",
        unregisteredCpf: 'Por favor, revise este campo e tente novamente.',
        minLength: "Dado inválido! Verifique sua resposta.",
        maxLength: "Dado inválido! Verifique sua resposta.",
        wrongData: "CPF/E-mail ou senha incorretos! Confira os dados preenchidos.",
        manyAttempts: "Você fez muitas tentativas incorretas, aguarde alguns minutos e tente novamente.",
        abandonedAccount: "Identificamos que sua conta não é acessada há um tempinho. Por favor, verifique o e-mail cadastrado e siga as instruções para redefinir a senha.",
        csrfExpired:
          `
            Atualize a página ou desabilite o bloqueio de cookies para entrar na sua conta.
            <a
              href='https://ajuda.melhorenvio.com.br/pt-BR/articles/6093620-como-se-cadastrar-e-fazer-login-na-plataforma-do-melhor-envio#h_d91daa10b9'
              target='_blank'
              rel="noopener noreferrer"
            >
              Como desbloquear os cookies?
            </a>
          `
      },
      errors: {
        username: "",
        password: "",
        iptPasswordReset: ""
      },

      focus: {
        username: false,
        password: false,
        iptPasswordReset: false
      },

      loading: false,
      hash: ""
    };
  },

  directives: {
    mask: mask
  },

  validations: {
    username: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(255)
    },
    password: {
      required,
      minLength: minLength(4),
      maxLength: maxLength(64)
    },
    iptPasswordReset: {
      requiredIf(value, self) {
        if (!self.modalPasswordReset) return true;
        return !!value;
      },
      document(value, self) {
        if (String(value).replace(/\D/g, '').length > 11) {
          return cnpj(value, self);
        }

        return cpf(value, self);
      }
    }
  },

  provide() {
    return {
      getSteps: this.get2faSteps,
      user: () => ({
        name: this.username,
        password: this.password,
        email: this.userEmail,
        phone: this.userPhone
      }),
      channel: () => this.selected2faChannel,
      remember: () => this.shouldRememberPassword
    }
  },

  created() {
    this.hash = location.hash;
    location.hash = "";
  },

  mounted() {
    this.autofillActiveClass();
  },

  computed: {
    activeSubmitButton() {
      if (this.$v.username.$error || this.$v.password.$error) {
        return "button-new--disabled";
      }

      return "button-new--primary";
    },

    activePasswordButton() {
      return this.iptPasswordReset && !this.$v.iptPasswordReset.$error
        ? "button-new--primary"
        : "button-new--disabled";
    },

    hasDocumentParam() {
      const params = new URL(document.location).searchParams;
      return params.get("action") === "documents";
    },

    get2faSteps() {
      return TWO_FACTOR_LOGIN_STEPS_ENUM;
    },

    shouldRememberPassword() {
      return this.remember ? "on" : "off"
    }
  },

  methods: {
    togglePasswordVisible() {
      this.passwordVisible = !this.passwordVisible;
    },

    isProductionEnvironment() {
      return MEConfig.get('app.env') === 'production';
    },

    submit() {
      this.$v.$touch();

      if (this.$v.username.$invalid || this.$v.password.$invalid) {
        Object.keys(this.$v.$params).forEach(e => {
          this.updateErrorMessage(e);
        });

        return;
      }

      this.invalidLogin = false;

      recaptcha('login', (token) => {
        this.handleLoading(true);

        axios
          .post(
            "/login",
            {
              username: this.username,
              password: this.password,
              remember: this.shouldRememberPassword,
              token: token,
            },
            {
              headers: {
                Accept: "application/json"
              }
            }
          )
          .then(response => {
            if (response.data && response.data['2fa_enabled']) {
              const {user} = response.data;

              this.userEmail = user.email;
              this.userPhone = user.phone;
              this.is2faCardVisible = true;

              return;
            }

            this.handleLoginRedirect(response.data);
          })
          .catch((error) => {
            this.invalidLogin = true;

            switch (error.response.status) {
              case 419:
                this.errorMessage = this.errorMessages.csrfExpired;
                break;
              case 422:
                if (error.response.data.message === this.errorMessages.abandonedAccount) {
                  this.$refs.revalidationModal.open();
                  break;
                }
                this.errorMessage = this.errorMessages.wrongData;
                break;
              case 429:
                this.errorMessage = this.errorMessages.manyAttempts;
                break;
            }
          })
          .finally(() => {
            this.handleLoading(false);
          });
        });
    },

    resendPassword() {
      this.$v.iptPasswordReset.$reset();
      this.$refs.passwordModal.open();
      this.modalPasswordReset = true;
    },

    sendResetPassword() {
      this.$v.iptPasswordReset.$touch();
      if (this.$v.iptPasswordReset.$invalid) {
        Object.keys(this.$v.$params).forEach(e => {
          this.updateErrorMessage(e);
        });
        this.handleLoading(false);
        return;
      }

      this.iptPasswordReset = String(this.iptPasswordReset).replace(/\D/g, "");
      this.handleLoading(true);

      axios
        .post(
          "/password/document",
          {
            document: this.iptPasswordReset
          },
          {
            headers: {
              Accept: "application/json"
            }
          }
        )
        .then(response => {
          if (response.data) {
            this.emailSended = true;
          } else {
            this.cpfNotFound = true;
          }
        })
        .catch(error => {
          switch (error.response.status) {
            case 404:
              this.cpfNotFound = true;
              break;

            case 419:
              this.handleToast({
                content: "Ops… aconteceu um erro! :/ Tente atualizar a página.",
                type: "danger"
              });
              break;

            case 429:
              this.handleToast({
                content: "Você fez muitas tentativas, tente novamente em alguns minutos.",
                type: "danger"
              });
              break;

            default:
              console.error(error);
              break;
          }
        })
        .finally(()=>{
          this.handleLoading(false);
        });
    },

    handleToast(args) {
      this.$refs.loginToast.createToast(args.content, args.type);
    },

    handleLoading(state) {
      this.loading = state;
    },

    handleLoginRedirect(data) {
      if (data && data.url) {
        this.redirectTo = data.url + this.hash;
      } else {
        this.redirectTo = "/painel";
      }

      this.$nextTick(() => {
        location.href = this.redirectTo;
      })
    },

    handle2faStep(step) {
      this.current2FAStep = step;
    },

    updateSelected2faChannel(channel) {
      this.selected2faChannel = channel;
    },

    iptFocus(ipt) {
      this.focus[ipt] = true;
    },

    iptBlur(ipt) {
      this.focus[ipt] = false;
      this.removeDocumentMask();
      this.updateErrorMessage(ipt);
    },

    updateErrorMessage(key) {
      if (!key) return;

      let errorTypes = Object.keys(this.errorMessages);
      for (let i = 0; i < errorTypes.length; i++) {
        let errorType = errorTypes[i];
        if (
          this.$v[key].hasOwnProperty(errorType) &&
          !this.$v[key][errorType]
        ) {
          this.errors[key] = "";
          if (this.errorMessages.hasOwnProperty(errorType)) {
            this.errors[key] = String(this.errorMessages[errorType]).replace(
              /__field__/,
              String(key)
                .replace(/^ipt/, "")
                .replace(/Change$/, "")
            );
          }
          break;
        }
      }
    },

    removeDocumentMask() {
      this.username = this.username.trim();

      if (!documents.isCpf(this.username) && !documents.isCnpj(this.username)) {
        return;
      }

      this.username = documents.unmask(this.username);
    },

    // Add 'active' class if fields are autofilled
    autofillActiveClass() {
      let i = 0;
      let handle = setInterval(() => {
        try {
          let el = $(":-webkit-autofill");
          el.each((i, e) => {
            $(e)
              .parent()
              .parent()
              .addClass("active");
          });
          i++;
          if (i === 15 || el.length) {
            clearInterval(handle);
          }
        } catch (e) {
          clearInterval(handle);
        }
      }, 100);
    }
  }
};
</script>

<style lang="stylus" scoped>
.forgot-password-body
  text-align center

.input-password-toggle
  position absolute
  padding 0
  right 0
  bottom 0
  z-index 1

  .ico
    width 40px
    height 30px
    fill #3598dc

#login-screen
  .form .formLine label
    z-index: 2

  .formLine
    width: 100%
    padding-top: 0 !important
    margin-top: 36px !important

    @media screen and (min-width: 720px)
      width: 328px

  #cpf-field
    .formLine
      margin-top: 20px !important

      @media screen and (min-width: 720px)
        margin-top: 36px !important

.login
  font-family: 'Lato', Arial, Helvetica, sans-serif
  display: flex
  flex-flow: row nowrap
  justify-content: center
  align-items: center

  &__subtitle
    font-size: 16px
    font-weight: bold
    line-height: 20px
    margin-top: 8px
    text-align: center

    @media screen and (min-width: 940px)
      margin-top: 24px

  .form
    width: 100%
    margin-bottom: 24px

    @media screen and (min-width: 940px)
      margin-bottom: 44px

    .error-message
      width: 100%
      font-size: 12px
      color: #e7505a
      max-height: 0
      opacity: 0

      &--visible
        margin-top: 28px
        max-height: 60px
        opacity: 1
        transition: all 0.1s ease-in-out

  .keep-connected
    width: 100%
    margin: 20px 0 28px

    input
      padding: 0
      display: none
      cursor: pointer

    label
      position: relative
      cursor: pointer
      font-family: 'Open Sans', sans-serif
      font-size: 14px
      color: #697882

      &:before
        content: ''
        -webkit-appearance: none
        background-color: #fff
        border: 2px solid #3598DC
        padding: 7px
        display: inline-block
        position: relative
        vertical-align: middle
        margin-top: -2px
        margin-right: 4px
        border-radius: 4px

    input:checked + label:before
      background-color: #3598DC

    input:checked + label:after
      content: ''
      display: block
      position: absolute
      top: 3px
      left: 6px
      width: 4px
      height: 8px
      border: solid #fff
      border-width: 0 2px 2px 0
      transform: rotate(45deg)

  /* change autofill bkg color */
  input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active
    -webkit-box-shadow: 0 0 0 30px #fff inset !important

  /* change autofill bkg color */
  input
    filter: none

  #forget-password, #revalidation-password
    // overrides default modal classes
    .modal-v2__card
      min-height: auto !important


    .modal-v2__card--medium
      min-height: auto !important


    .custom-content
      color: #3B3F51

      &__header
        width: 100%
        display: flex
        flex-flow: column nowrap
        justify-items: center
        align-items: center
        margin-bottom: 28px

        @media screen and (min-width: 940px)
          margin-bottom: 32px

        h1
          font-size: 20px
          font-weight: bold
          line-height: 24px
          margin-bottom: 16px

        img
          height: 160px
          margin-bottom: 12px
          display: none

          @media screen and (min-width: 940px)
            display: block

        p
          text-align: center
          font-size: 14px
          line-height: 18px

          strong
            font-weight: bold

          @media screen and (min-width: 940px)
            max-width: 436px

      &__body
        display: flex
        flex-flow: column nowrap
        justify-content: flex-start
        align-items: center

      &__text
        max-width: 424px
        margin-bottom: 24px
        font-size: 12px
        color: #697882

        &--small
          max-width: 354px
          text-align: center

      &__text-wrapper
        display: flex
        justify-content: flex-start
        align-items: flex-start

      .alert-icon
        color: #FE9037 !important
        width: 16px
        height: 16px
        margin-right: 8px

      &__footer
        display: flex
        justify-content: center

      // overrides default modal classes
      .modal-v2__body
        min-height: 130px

        @media screen and (min-width: 720px)
          padding: 0 56px

        &--centered
          text-align: center !important

        &--small
          min-height: auto

      .modal-v2__footer
        padding-top: 28px
</style>
